import { useContext, useEffect, useMemo, useState } from 'react';
import { CollectionsContext } from './Context';
import {
  createUserPacketCollection,
  deleteUserPacketCollection,
  getUserPacketCollections,
  leaveFolder,
  updateUserPacketCollection,
} from '../../../services/UserPacketCollectionService';
import { useAuth } from '@clerk/react';

export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return context;
};

export const CollectionsProvider = ({ children }) => {
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isSignedIn } = useAuth();
  useEffect(() => {
    const fetchCollections = async () => {
      if (!isSignedIn) {
        return;
      }
      setIsLoading(true);
      const response = await getUserPacketCollections();
      setCollections(response.data.results);
      setIsLoading(false);
    };
    fetchCollections();
  }, [isSignedIn]);

  const selectors = useMemo(
    () => ({
      collections,
      isLoading,
      getCollectionById: (collectionId) => {
        return collections.find((c) => c.id === collectionId);
      },
    }),
    [collections, isLoading]
  );

  const mutators = useMemo(
    () => ({
      createCollection: async ({ title, packet_id, description }) => {
        const response = await createUserPacketCollection(title, packet_id, description);
        // A folder you create is one you own (the list endpoint tags fetched folders, but
        // create/add return the raw collection — mark it owned so it lands in the right group).
        const newCollection = { ...response.data.results, is_owner: true };
        // set the collection alphabetically in the array
        setCollections((prev) =>
          [...prev, newCollection].sort((a, b) => a.title.localeCompare(b.title))
        );
        return newCollection;
      },
      addCollection: (newCollection) => {
        setCollections((prev) =>
          [...prev, { is_owner: true, ...newCollection }].sort((a, b) =>
            a.title.localeCompare(b.title)
          )
        );
      },
      updateCollectionName: async ({ collectionId, title, description }) => {
        const response = await updateUserPacketCollection(collectionId, { title, description });
        const updatedCollection = response.data.results;
        // Renaming is owner-only; preserve the ownership tags the list endpoint set.
        setCollections((prev) =>
          prev.map((c) =>
            c.id === collectionId
              ? { ...updatedCollection, is_owner: c.is_owner, owner_name: c.owner_name }
              : c
          )
        );
        return updatedCollection;
      },
      deleteCollection: async (collectionId) => {
        await deleteUserPacketCollection(collectionId);
        setCollections((prev) => prev.filter((c) => c.id !== collectionId));
      },
      leaveCollection: async (collectionId) => {
        await leaveFolder(collectionId);
        setCollections((prev) => prev.filter((c) => c.id !== collectionId));
      },
    }),
    []
  );

  return (
    <CollectionsContext.Provider value={{ ...selectors, ...mutators }}>
      {children}
    </CollectionsContext.Provider>
  );
};
